import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, Check, X } from 'lucide-react';

// Downscale in halving steps so large images stay smooth instead of jagged
const smoothSource = (img, targetScale) => {
  let src = img;
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  while (targetScale < 0.5 && w > 2 && h > 2) {
    w = Math.round(w / 2);
    h = Math.round(h / 2);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const cx = c.getContext('2d');
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(src, 0, 0, w, h);
    src = c;
    targetScale *= 2;
  }
  return { src };
};

const cropToFile = (src, area, outW, outH, name, transparent) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!transparent) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outW, outH);
    }
    const sx = outW / area.width;
    const sy = outH / area.height;
    const { src: source } = smoothSource(img, Math.min(sx, sy));
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, -area.x * sx, -area.y * sy, img.naturalWidth * sx, img.naturalHeight * sy);
    canvas.toBlob((blob) => blob ? resolve(new File([blob], name, { type: 'image/png' })) : reject(new Error('crop')), 'image/png');
  };
  img.onerror = reject;
  img.src = src;
});

const ImageCropper = ({ file, onCancel, onConfirm, outW, outH, title, hint, fileName, testIdPrefix, transparent = false }) => {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);
  const confirm = async () => onConfirm(await cropToFile(src, area, outW, outH, fileName, transparent));

  return (
    <div data-testid={`${testIdPrefix}-modal`} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: '12px', width: '100%', maxWidth: '640px', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ fontWeight: 600, color: '#2D3748' }}>{title}</h3>
          <p style={{ fontSize: '0.8rem', color: '#718096' }}>{hint}</p>
        </div>
        <div style={{ position: 'relative', height: '360px', background: '#1a202c' }}>
          {src && (
            <Cropper image={src} crop={crop} zoom={zoom} minZoom={0.3} maxZoom={5} aspect={outW / outH} restrictPosition={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem' }}>
          <ZoomOut className="w-4 h-4" style={{ color: '#718096' }} />
          <input type="range" min={0.3} max={5} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: '#68AAB0' }} data-testid={`${testIdPrefix}-zoom`} />
          <ZoomIn className="w-4 h-4" style={{ color: '#718096' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0 1.25rem 1.25rem' }}>
          <Button type="button" variant="outline" onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}><X className="w-4 h-4 mr-2" />Cancelar</Button>
          <Button type="button" onClick={confirm} disabled={!area} style={{ background: '#68AAB0' }} data-testid={`${testIdPrefix}-confirm`}><Check className="w-4 h-4 mr-2" />Aplicar recorte</Button>
        </div>
      </div>
    </div>
  );
};

export const HeaderImageCropper = (props) => (
  <ImageCropper {...props} outW={840} outH={300} transparent title="Ajustar imagem do cabeçalho" fileName="header.png" testIdPrefix="header-cropper"
    hint="Arraste para posicionar e use o zoom (diminua para caber inteira). Exibida em 280 × 100, em alta definição e mantendo as áreas transparentes." />
);

export const CoverImageCropper = (props) => (
  <ImageCropper {...props} outW={380} outH={550} title="Ajustar capa do livro" fileName="capa.png" testIdPrefix="cover-cropper"
    hint="Arraste para posicionar e use o zoom para a capa caber na moldura sem distorcer." />
);
